import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Download, Loader2, RefreshCw } from "lucide-react";
import {
  adminApi,
  STATUSES,
  STAGES,
  STATUS_STYLES,
} from "@/lib/adminApi";

const cols = [
  "Created",
  "Name",
  "Phone",
  "Email",
  "Focus",
  "Capital",
  "Timeline",
  "Experience",
  "Location",
  "Status",
  "Stage",
  "Notes",
];

export default function LeadsTable() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/leads");
      setLeads(data);
    } catch {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-refresh: refetch when the tab regains focus or becomes visible,
  // and on a light 45s interval — so counselors see new leads without
  // having to click Refresh.
  useEffect(() => {
    const onFocus = () => load();
    const onVis = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    const id = setInterval(load, 45000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = async (id, field, value) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
    try {
      await adminApi.patch(`/admin/leads/${id}`, { [field]: value });
      toast.success(`Updated ${field}`);
    } catch {
      setLeads(prev);
      toast.error(`Could not update ${field}`);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await adminApi.get("/admin/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bullsedge_leads_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (stageFilter !== "all" && l.stage !== stageFilter) return false;
      if (!q) return true;
      return [l.name, l.phone, l.email].some((v) =>
        (v || "").toLowerCase().includes(q)
      );
    });
  }, [leads, query, statusFilter, stageFilter]);

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "-";

  return (
    <div data-testid="leads-table-wrap">
      {/* toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <div className="flex min-h-[42px] flex-1 items-center rounded-xl border border-slate-200 bg-white px-3 sm:max-w-xs">
            <Search size={16} className="mr-2 text-slate-400" />
            <input
              data-testid="leads-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, email"
              className="w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
          </div>
          <select
            data-testid="filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="min-h-[42px] rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            data-testid="filter-stage"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="min-h-[42px] rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none"
          >
            <option value="all">All stages</option>
            {STAGES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="refresh-leads"
            className="flex min-h-[42px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:border-emerald-600/50"
          >
            <RefreshCw size={15} /> Refresh
          </button>
          <button
            onClick={exportXlsx}
            disabled={exporting}
            data-testid="export-leads-btn"
            className="flex min-h-[42px] items-center gap-2 rounded-xl bg-emerald-brand px-4 text-sm font-bold text-white transition-transform hover:scale-[1.02] disabled:opacity-70"
          >
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            Export Excel
          </button>
        </div>
      </div>

      <p className="mt-3 text-sm text-slate-500" data-testid="leads-count">
        Showing {filtered.length} of {leads.length} leads
      </p>

      {/* table */}
      <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
            <Loader2 className="animate-spin" size={18} /> Loading leads…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-500">No leads found.</div>
        ) : (
          <table className="w-full min-w-[1100px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {cols.map((c) => (
                  <th
                    key={c}
                    className="whitespace-nowrap px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-500"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr
                  key={l.id}
                  data-testid={`lead-row-${l.id}`}
                  className="border-b border-slate-100 align-top hover:bg-slate-50/60"
                >
                  <td className="whitespace-nowrap px-3 py-3 text-slate-500">{fmtDate(l.created_at)}</td>
                  <td className="px-3 py-3 font-semibold text-slate-900">{l.name || "-"}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-slate-700">{l.phone}</td>
                  <td className="px-3 py-3 text-slate-600">{l.email || "-"}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-slate-600">{l.focus || "-"}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-slate-600">{l.capital || "-"}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-slate-600">{l.timeline || "-"}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-slate-600">{l.experience || "-"}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-slate-600">{l.location_pref || "-"}</td>
                  <td className="px-3 py-3">
                    <select
                      data-testid={`status-select-${l.id}`}
                      value={l.status}
                      onChange={(e) => update(l.id, "status", e.target.value)}
                      className={`rounded-full border px-2.5 py-1 text-xs font-bold capitalize focus:outline-none ${STATUS_STYLES[l.status] || "border-slate-200 text-slate-700"}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <select
                      data-testid={`stage-select-${l.id}`}
                      value={l.stage}
                      onChange={(e) => update(l.id, "stage", e.target.value)}
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-emerald-brand focus:outline-none"
                    >
                      {STAGES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <textarea
                      data-testid={`notes-input-${l.id}`}
                      defaultValue={l.notes}
                      rows={2}
                      onBlur={(e) => {
                        if (e.target.value !== (l.notes || "")) update(l.id, "notes", e.target.value);
                      }}
                      placeholder="Add note…"
                      className="min-w-[200px] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-emerald-brand focus:outline-none"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
